import { createTargetObjectCanvas_CanvasItem_Props } from '@/types/productV2'
import drawFileToCanvas from '@/components/ProductProfile/utils/drawSection/drawFileToCanvas'

/**
 * Create a custom canvas with drawn boxes and text
 * @param name of canvas
 * @param size of canvas
 * @param items List
 * @param scale Scaling factor for drawing
 * @param singleSection Whether to draw a single section or multiple
 * @returns HTMLCanvasElement will be created and drawn upon
 */

type CreateTargetObjectCanvasArgs = {
  name: string
  size: {
    width: number
    height: number
  }
  items: createTargetObjectCanvas_CanvasItem_Props[]
  scale?: number
  singleSectionId?: string | null
}

export default async function createTargetObjectCanvas({
  name,
  size,
  items,
  scale = 20,
  singleSectionId = null,
}: CreateTargetObjectCanvasArgs): Promise<HTMLCanvasElement | null> {
  let canvas = document.querySelector<HTMLCanvasElement>(`#targetObjectCanvas-${name}`)
  if (!canvas) {
    // Dynamically create canvas (used for back-face canvases)
    canvas = document.createElement('canvas')
    canvas.id = `targetObjectCanvas-${name}`
    canvas.style.display = 'none'
    document.body.appendChild(canvas)
  }

  canvas.width = size.width * scale
  canvas.height = size.height * scale

  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // items.forEach((item) => {
  for (const item of items) {
    if (singleSectionId && item.sectionId !== singleSectionId) continue

    const boxWidth = item.width * scale
    const boxHeight = item.height * scale
    const x = item.x * scale
    const y = item.y * scale

    ctx.save()

    ctx.translate(x, y)

    ctx.globalCompositeOperation = 'source-over'
    ctx.fillStyle = 'black'
    ctx.fillRect(0, 0, boxWidth, boxHeight)

    ctx.globalCompositeOperation = 'destination-out'

    if (item.mask) {
      await drawFileToCanvas(ctx, item.mask, boxWidth, boxHeight)
    }

    // --- START: Arrow Drawing ---
    // These arrows will be "cut out" of the black box above

    const arrowFix = scale / 2
    const arrowSize = 4 * scale // How long the arrow "barbs" are
    const arrowLineWidth = scale // How thick the arrow lines are
    const centerX = boxWidth / 2
    const centerY = boxHeight / 2

    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    ctx.lineWidth = arrowLineWidth
    ctx.lineCap = 'square' // Makes the arrow tips and joins look smoother

    // The color doesn't matter for 'destination-out',
    // but it's good practice to set it.
    ctx.strokeStyle = 'white'

    ctx.beginPath()

    // Top Arrow (pointing up)
    ctx.moveTo(centerX - arrowSize, arrowSize) // Top-left barb
    ctx.lineTo(centerX, arrowFix) // Tip at top edge
    ctx.lineTo(centerX + arrowSize, arrowSize) // Top-right barb

    // Bottom Arrow (pointing down)
    ctx.moveTo(centerX - arrowSize, boxHeight - arrowSize) // Bottom-left barb
    ctx.lineTo(centerX, boxHeight - arrowFix) // Tip at bottom edge
    ctx.lineTo(centerX + arrowSize, boxHeight - arrowSize) // Bottom-right barb

    // Left Arrow (pointing left)
    ctx.moveTo(arrowSize, centerY - arrowSize) // Left-top barb
    ctx.lineTo(arrowFix, centerY) // Tip at left edge
    ctx.lineTo(arrowSize, centerY + arrowSize) // Left-bottom barb

    // Right Arrow (pointing right)
    ctx.moveTo(boxWidth - arrowSize, centerY - arrowSize) // Right-top barb
    ctx.lineTo(boxWidth - arrowFix, centerY) // Tip at right edge
    ctx.lineTo(boxWidth - arrowSize, centerY + arrowSize) // Right-bottom barb

    ctx.stroke() // Applies the drawing, cutting out the lines
    // --- END: Arrow Drawing ---

    const title = createFittedText(item.name || '...', boxWidth * 0.8, boxHeight * 0.35)
    ctx.drawImage(title, centerX - title.width / 2, centerY - title.height / 2, title.width, title.height)

    const description = createText(`(${item.description})`)
    const descriptionWidth = Math.min(boxWidth * 0.8, canvas.width / 3) * 0.3
    const descriptionHeight = (descriptionWidth * description.height) / description.width
    ctx.drawImage(
      description,
      centerX - descriptionWidth / 2,
      centerY - descriptionHeight / 2 + title.height,
      descriptionWidth,
      descriptionHeight
    )

    ctx.globalCompositeOperation = 'source-over'

    ctx.restore()
  }

  return canvas
}

function createText(text: string) {
  const textCanvas = document.createElement('canvas')
  const tctx = textCanvas.getContext('2d')!

  const fontStyle = 'bold 300px Arial'
  tctx.font = fontStyle

  const metrics = tctx.measureText(text)
  const w = Math.ceil(metrics.width)
  const h = Math.ceil(metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent)

  textCanvas.width = w
  textCanvas.height = h

  tctx.font = fontStyle
  tctx.fillStyle = '#000'
  tctx.fillText(text, 0, metrics.actualBoundingBoxAscent)

  return textCanvas
}

function splitTextAtMidpoint(text: string): [string, string] {
  const mid = Math.ceil(text.length / 2)
  // Look for a word boundary (space, underscore, hyphen) near the midpoint
  const searchRange = 3
  for (let offset = 0; offset <= searchRange; offset++) {
    for (const idx of [mid + offset, mid - offset]) {
      if (idx > 0 && idx < text.length && /[\s_\-]/.test(text[idx])) {
        return [text.slice(0, idx), text.slice(idx + (text[idx] === ' ' ? 1 : 0))]
      }
    }
  }
  return [text.slice(0, mid), text.slice(mid)]
}

function createFittedText(text: string, maxWidth: number, maxHeight: number) {
  const textCanvas = document.createElement('canvas')
  const tctx = textCanvas.getContext('2d')!

  const refFontSize = 300
  const fontStyle = `bold ${refFontSize}px Arial`
  tctx.font = fontStyle

  const singleMetrics = tctx.measureText(text)
  const singleW = singleMetrics.width
  const singleH = singleMetrics.actualBoundingBoxAscent + singleMetrics.actualBoundingBoxDescent

  // Standard scale: fit text to maxWidth/maxHeight
  const standardScale = Math.min(maxWidth / singleW, maxHeight / singleH)
  // Minimum scale: half of standard
  const minScale = standardScale * 0.5

  // Try to fit single line: shrink down to half standard scale
  // Text fits if at the shrunk scale the width still stays within maxWidth
  const fitsAtMinScale = singleW * minScale <= maxWidth * 0.85

  if (fitsAtMinScale || text.length <= 6) {
    // Single line — use standard scale (capped by maxWidth/maxHeight)
    const scale = Math.min(maxWidth / singleW, maxHeight / singleH)
    const w = Math.ceil(singleW)
    const h = Math.ceil(singleH)
    textCanvas.width = Math.ceil(w * scale)
    textCanvas.height = Math.ceil(h * scale)
    tctx.font = fontStyle
    tctx.fillStyle = '#000'
    tctx.setTransform(scale, 0, 0, scale, 0, 0)
    tctx.fillText(text, 0, singleMetrics.actualBoundingBoxAscent)
    tctx.setTransform(1, 0, 0, 1, 0, 0)
    return textCanvas
  }

  // Two-line rendering at half standard size
  const [line1, line2] = splitTextAtMidpoint(text)
  const m1 = tctx.measureText(line1)
  const m2 = tctx.measureText(line2)
  const lineW = Math.max(m1.width, m2.width)
  const lineH = m1.actualBoundingBoxAscent + m1.actualBoundingBoxDescent
  const lineGap = lineH * 0.3
  const totalH = lineH * 2 + lineGap
  const totalW = lineW

  const scale = Math.min(maxWidth / totalW, maxHeight / totalH)
  textCanvas.width = Math.ceil(totalW * scale)
  textCanvas.height = Math.ceil(totalH * scale)

  tctx.font = fontStyle
  tctx.fillStyle = '#000'
  tctx.textAlign = 'center'
  tctx.setTransform(scale, 0, 0, scale, 0, 0)
  tctx.fillText(line1, totalW / 2, m1.actualBoundingBoxAscent)
  tctx.fillText(line2, totalW / 2, lineH + lineGap + m2.actualBoundingBoxAscent)
  tctx.setTransform(1, 0, 0, 1, 0, 0)

  return textCanvas
}
